import React, { useRef, useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLogout, useUser } from '../../../hooks/useAuth';
import { sec1, dummyUser, logoColor } from '../../ui/media';
import { useSubscriptionPackagesListQuery } from '../../../hooks/useSubscriptions'
import toast from 'react-hot-toast';

const SubscriptionPricingPage = () => {
    const [activeTab, setActiveTab] = useState('Event Companies');
    const navigate = useNavigate()
    const dropdownRef = useRef(null);
    const [subscriptionPackagesList, setSubscriptionPackagesList] = useState([]);
    const [userData, setUserData] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    const logout = useLogout()
    const user = useUser()
    const { isError, data } = user
    const { data: subscriptionPackagesResponse, isSuccess: subscriptionPackagesSuccess } = useSubscriptionPackagesListQuery(
        activeTab ? `?product_type=${activeTab == "Event Companies" ? "event" : "security"}` : null,
        {
            enabled: !!activeTab,
        }
    );

    const toggleNav = () => {
        setNavOpen(!navOpen);
    };

    const handleLogout = () => {
        logout.mutate(null, {
            onSuccess: () => {
                navigate('/');
                toast.success('Logged out successfully');
            },
            onError: () => {
                navigate('/');
                toast.error('Session expired, logged out');
            }
        })
    };

    useEffect(() => {
        if (isError) {
            handleLogout()
        }
        setUserData(data?.data?.data)
    }, [data, isError])

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (subscriptionPackagesSuccess) {
            setSubscriptionPackagesList(subscriptionPackagesResponse?.data?.data)
        }
    }, [subscriptionPackagesSuccess, subscriptionPackagesResponse])

    return (
        <div style={{ background: `url(${sec1})`, backgroundSize: "100% 100%" }} className="min-h-screen relative overflow-hidden">
            {/* Navigation */}
            <header className="sticky top-0 z-50 shadow-md bg-white/50">
                <div className="container mx-auto px-4 py-3 flex justify-between items-center">
                    <div className="flex items-center">
                        <img className="h-12" src={logoColor} alt="Logo" />
                    </div>
                    <button onClick={toggleNav} className="md:hidden text-white">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                        </svg>
                    </button>
                    <nav className={`absolute md:relative top-full left-0 w-full md:w-auto md:bg-transparent p-4 md:p-0 transform ${navOpen ? 'scale-y-100' : 'scale-y-0 md:scale-y-100'} origin-top transition-transform md:flex md:items-center`}>
                        <ul className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-6">
                            {[{
                                name: 'Home',
                                url: '/lander'
                            }, {
                                name: 'About Us',
                                url: '/lander#about'
                            }, {
                                name: 'Services',
                                url: '/lander#services',
                            }, {
                                name: 'Subscriptions',
                                url: '/subscriptions',
                            }, {
                                name: 'FAQ',
                                url: '/lander#faq'
                            }, {
                                name: 'Contact Us',
                                url: '/lander#contact'
                            }].map((item, index) => (
                                <li key={index?.url}>
                                    <a href={`${item.url}`} className={`text-gray-800`}>{item?.name}</a>
                                </li>
                            ))}
                            {userData &&
                                <li key={'dashboard'}>
                                    <a href={`${userData?.user_type === 2 ? '/events/list' : '/locations/list'}`} className={`text-gray-800`}>Dashboard</a>
                                </li>
                            }
                        </ul>
                        {userData ?
                            <div className="mt-4 md:mt-0 px-5 py-1 relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                    className={`flex items-center font-medium text-gray-800 focus:outline-none`}>
                                    <img
                                        className="h-8 rounded-full mr-2"
                                        src={userData?.profile_pic ? `${process.env.REACT_APP_IMAGE_URL}/${userData?.profile_pic}` : dummyUser}
                                        alt="Profile"
                                    />
                                    <span className="hidden md:block">{userData?.name}</span>
                                    <svg
                                        className="ml-2 h-5 w-5 text-gray-800"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true">
                                        <path
                                            fillRule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                                {dropdownOpen && (
                                    <div className={`absolute right-0 mt-2 w-auto rounded-md shadow-lg py-1 bg-white focus:outline-none`}>
                                        <div className="px-4 py-3">
                                            <p className={`text-sm text-gray-800`}>{userData?.name}</p>
                                            <p className={`text-sm text-gray-800 font-medium text-gray-500 truncate`}>
                                                {userData?.email}
                                            </p>
                                        </div>
                                        <hr />
                                        <a
                                            href={`${userData?.user_type === 2 ? "/events/list" : "/locations/list"}`}
                                            className={`block px-4 py-2 text-sm text-black hover:text-black`}>
                                            Back to Dashboard
                                        </a>
                                        <a
                                            href="/account"
                                            className={`block px-4 py-2 text-sm text-black hover:text-black`}>
                                            Account
                                        </a>
                                        <button
                                            onClick={handleLogout}
                                            className={`block w-full text-left px-4 py-2 text-sm text-black hover:text-black`}>
                                            Log Out
                                        </button>
                                    </div>
                                )}
                            </div> :
                            <button
                                onClick={() => navigate('/login')}
                                className="mt-4 md:mt-0 md:ml-6 bg-blue-600 text-white px-5 py-1 rounded-full font-medium">
                                Log In
                            </button>
                        }
                    </nav>
                </div>
            </header>
            {/* Main Content */}
            <div className="relative z-10 container mx-auto px-4 py-12">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">
                        SUBSCRIPTION PLANS
                    </h1>
                    {/* Tab Navigation */}
                    <div className="flex justify-center mb-8">
                        <div className="w-full bg-white bg-opacity-80 backdrop-blur-sm rounded-xl p-1 flex">
                            {['Event Companies', 'Security Companies'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-8 py-3 rounded-xl w-full font-medium transition-all duration-300 ${activeTab === tab
                                        ? 'bg-blue-600 text-white shadow-lg'
                                        : 'text-gray-900 hover:bg-white hover:bg-opacity-10'
                                        }`}>
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {/* Pricing Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                    {subscriptionPackagesList?.map((plan) => (
                        <div
                            key={plan.id}
                            className={`relative bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer shadow-xl`}>
                            {/* Plan Header */}
                            <div className="text-center mb-6">
                                <h3 className="text-lg font-bold text-blue-600 mb-2 tracking-wider">
                                    {plan?.description}
                                </h3>
                                <div className="flex items-baseline justify-center">
                                    <span className="text-3xl font-bold text-gray-900">$</span>
                                    <span className="text-4xl font-bold text-gray-900">{plan?.price_value}</span>
                                </div>
                            </div>
                            {/* Features List */}
                            <div className="space-y-3 mb-6">
                            {activeTab == "Event Companies" ?
                                <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center mt-0.5">
                                        <Check className="w-3 h-3 text-white" />
                                    </div>
                                    <span className="text-sm text-gray-700 leading-relaxed">
                                        User Limit : {plan?.metadata?.user_limit} Users
                                    </span>
                                </div>
                                :
                                <>
                                    <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center mt-0.5">
                                            <Check className="w-3 h-3 text-white" />
                                        </div>
                                        <span className="text-sm text-gray-700 leading-relaxed">
                                            Camera Limit : {plan?.metadata?.camera_limit}
                                        </span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center mt-0.5">
                                            <Check className="w-3 h-3 text-white" />
                                        </div>
                                        <span className="text-sm text-gray-700 leading-relaxed">
                                            Duration : {plan?.metadata?.duration} Month{plan?.metadata?.duration>1?'s': ''}
                                        </span>
                                    </div>
                                </>
                            }   
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-blue-600 to-transparent opacity-0 hover:opacity-10 transition-opacity duration-300 rounded-2xl pointer-events-none"></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPricingPage;